import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { IAuthenticatedModel } from 'src/app/store/login/login.models';
import { selectLoginInfo } from 'src/app/store/login/login.selectors';
import { loadLogin, logout, resetLogin } from 'src/app/store/login/login.actions';
import { resetCatalogs } from 'src/app/store/catalogs/catalogs.actions';
import { resetCatalogsVehicle } from 'src/app/store/catalog-vehicle/catalog-vehicle.actions';
import { resetGroupsCatalog } from 'src/app/store/group-catalogs/group-catalogs.actions';

export const CATALOG_CACHE_INVALID_KEY = 'arkik.catalog-cache-invalid';

export interface CatalogSessionContext {
  readonly userGuid: string;
  readonly companyGuid: string;
  readonly epoch: number;
}

@Injectable({ providedIn: 'root' })
export class CatalogSessionService {
  private authenticated: IAuthenticatedModel | undefined;
  private captured?: CatalogSessionContext;
  private readonly cancellation = new Subject<void>();
  readonly cancelled$ = this.cancellation.asObservable();
  epoch = 0;
  loggingOut = false;
  private invalid = false;
  private markerFailure?: Error;
  private logoutSequence = 0;

  constructor(
    private store: Store,
    actions$: Actions,
  ) {
    this.store.select(selectLoginInfo).subscribe((authenticated) => {
      const identityChanged =
        this.captured &&
        (authenticated?.UserGuid?.toUpperCase() !== this.captured.userGuid ||
          (authenticated?.CompanyGuid?.toUpperCase() ?? '') !== this.captured.companyGuid);
      this.authenticated = authenticated;
      if (identityChanged) {
        this.persistInvalidation();
        this.invalidate();
      }
    });
    actions$.pipe(ofType(loadLogin, logout, resetLogin)).subscribe((action) => {
      if (action.type === logout.type) {
        this.beginLogout();
        return;
      }
      this.invalidate();
      if (action.type === loadLogin.type) {
        this.loggingOut = false;
      }
    });
  }

  context(): CatalogSessionContext {
    if (this.loggingOut) {
      throw new Error('Catalog access is blocked during logout');
    }
    if (!this.captured) {
      const userGuid = this.authenticated?.UserGuid;
      if (
        typeof userGuid !== 'string' ||
        !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(userGuid) ||
        userGuid === '00000000-0000-0000-0000-000000000000'
      ) {
        throw new Error('Authenticated session has no valid nonzero UserGuid');
      }
      this.captured = Object.freeze({
        userGuid: userGuid.toUpperCase(),
        companyGuid: this.authenticated?.CompanyGuid?.toUpperCase() ?? '',
        epoch: this.epoch,
      });
    }
    return this.captured;
  }

  invalidate(): void {
    this.epoch++;
    this.captured = undefined;
    this.cancellation.next();
    this.resetStores();
  }

  resetStores(): void {
    this.store.dispatch(resetCatalogs());
    this.store.dispatch(resetCatalogsVehicle());
    this.store.dispatch(resetGroupsCatalog());
  }

  beginLogout(): void {
    if (this.loggingOut) {
      return;
    }
    this.persistInvalidation();
    this.loggingOut = true;
    this.logoutSequence++;
    this.invalidate();
  }

  get logoutGeneration(): number {
    return this.logoutSequence;
  }

  private persistInvalidation(): void {
    // This key is deliberately outside IDB, sessionStorage and MSAL's key space.
    this.invalid = true;
    try {
      localStorage.setItem(CATALOG_CACHE_INVALID_KEY, '1');
      if (localStorage.getItem(CATALOG_CACHE_INVALID_KEY) !== '1') {
        throw new Error('Catalog cache invalidation marker was not persisted');
      }
      this.markerFailure = undefined;
    } catch (error) {
      this.markerFailure = new Error(`Cannot persist catalog cache invalidation: ${String(error)}`);
      console.error(this.markerFailure);
    }
  }

  needsCleanup(): boolean {
    try {
      return this.invalid || localStorage.getItem(CATALOG_CACHE_INVALID_KEY) !== null;
    } catch (error) {
      throw new Error(`Cannot read catalog cache invalidation marker: ${String(error)}`);
    }
  }

  cleanupSucceeded(): void {
    localStorage.removeItem(CATALOG_CACHE_INVALID_KEY);
    if (localStorage.getItem(CATALOG_CACHE_INVALID_KEY) !== null) {
      throw new Error('Cannot remove catalog cache invalidation marker');
    }
    this.invalid = false;
    this.markerFailure = undefined;
  }

  assertCurrent(epoch: number): void {
    if (this.loggingOut || epoch !== this.epoch) {
      throw new Error('Catalog operation belongs to a previous session');
    }
  }

  assertDurableInvalidation(): void {
    if (this.markerFailure) {
      throw this.markerFailure;
    }
    if (localStorage.getItem(CATALOG_CACHE_INVALID_KEY) !== '1') {
      throw new Error('Catalog cache invalidation marker is missing');
    }
  }
}
